import { useEffect, useState, useCallback, useRef } from 'react'

type OrientationType = 'portrait' | 'landscape' | 'unknown'
type OrientationChangeOptions = {
  /**
   * If true, will automatically reload the page on orientation change
   * @default false
   */
  reloadOnChange?: boolean
  
  /**
   * Callback function to be called when orientation changes
   * Receives the new orientation as a parameter
   */
  onOrientationChange?: (orientation: OrientationType) => void
  
  /**
   * Delay in milliseconds before triggering the reload or callback
   * Useful to allow animations to complete
   * @default 0
   */
  delay?: number
  
  /**
   * If true, will only trigger on the first orientation change
   * @default false
   */
  once?: boolean
}

/**
 * Hook to detect device orientation changes and optionally reload the page
 * 
 * @param options Configuration options
 * @returns Object containing the current orientation and a function to manually reload
 */
export function useOrientationChange(options: OrientationChangeOptions = {}) {
  const { 
    reloadOnChange = false, 
    onOrientationChange,
    delay = 0,
    once = false
  } = options
  
  const [orientation, setOrientation] = useState<OrientationType>('unknown')
  const hasTriggeredRef = useRef(false)
  
  // Get the current orientation
  const getOrientation = useCallback((): OrientationType => {
    // Check if window.orientation is available (older API)
    if (typeof window.orientation !== 'undefined') {
      const angle = window.orientation as number
      return (angle === 0 || angle === 180) ? 'portrait' : 'landscape'
    }
    
    // Use newer screen.orientation API if available
    if (window.screen && window.screen.orientation) {
      const type = window.screen.orientation.type
      return type.includes('portrait') ? 'portrait' : 'landscape'
    }
    
    // Fallback to media query
    return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape'
  }, [])
  
  // Handle reload
  const handleReload = useCallback(() => {
    window.location.reload()
  }, [])
  
  // Function to manually trigger reload
  const reload = useCallback(() => {
    handleReload()
  }, [handleReload])
  
  // Handle orientation change
  const handleOrientationChange = useCallback(() => {
    const newOrientation = getOrientation()
    
    // Only proceed if orientation has actually changed
    if (orientation !== 'unknown' && orientation !== newOrientation) {
      // If once is true and we've already triggered, don't do anything
      if (once && hasTriggeredRef.current) {
        return
      }
      
      hasTriggeredRef.current = true
      
      // Set the new orientation
      setOrientation(newOrientation)
      
      // Execute callback or reload after delay
      if (delay > 0) {
        setTimeout(() => {
          if (reloadOnChange) {
            handleReload()
          } else if (onOrientationChange) {
            onOrientationChange(newOrientation)
          }
        }, delay)
      } else {
        if (reloadOnChange) {
          handleReload()
        } else if (onOrientationChange) {
          onOrientationChange(newOrientation)
        }
      }
    } else if (orientation === 'unknown') {
      // Initial orientation setting
      setOrientation(newOrientation)
    }
  }, [orientation, getOrientation, reloadOnChange, onOrientationChange, handleReload, delay, once])
  
  // Set initial orientation and add event listeners
  useEffect(() => {
    // Set initial orientation
    setOrientation(getOrientation())
    
    // Add event listeners for both old and new APIs
    window.addEventListener('orientationchange', handleOrientationChange)
    
    // For newer browsers that support screen.orientation
    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange)
    }
    
    // Also listen for resize events as a fallback
    window.addEventListener('resize', handleOrientationChange)
    
    // Cleanup
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      
      if (window.screen && window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange)
      }
      
      window.removeEventListener('resize', handleOrientationChange)
    }
  }, [getOrientation, handleOrientationChange])
  
  return { 
    orientation,
    reload
  }
}

export default useOrientationChange
